import { Close, History, PersonOutline, Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Switch,
  Typography,
} from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { P } from "const/permissions";
import { usePermission } from "hooks/usePermissions";
import { format } from "date-fns";
import { useState } from "react";
import { toast } from "react-toastify";
import { fetchApplicationProvenance, setApplicationVisibility } from "./referralApplicationApi";

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return format(date, "dd MMM yyyy, HH:mm");
};

const ProvenanceLine = ({ label, who, when }) => (
  <div className="min-w-0">
    <dt className="text-[11px] uppercase tracking-wide text-app-text-muted font-semibold">
      {label}
    </dt>
    <dd className="m-0 mt-0.5 text-sm text-app-text">
      {who || "—"}
      {when && <span className="text-app-text-secondary"> · {formatDateTime(when)}</span>}
    </dd>
  </div>
);

/**
 * Where an application entry came from and who may see it. Requested entries name the partner-side
 * requester and the office approver; direct entries name the staff member who created them. The
 * portal-sharing switch lives here so origin and visibility are reviewed together.
 */
const ApplicationProvenanceDialog = ({ application, onClose, clientId }) => {
  const queryClient = useQueryClient();
  const canReview = usePermission(P.REFERRAL.APPLICATION_REVIEW);
  const [busy, setBusy] = useState(false);

  const module = application?.module;
  const entryId = application?.id;

  const { data: provenance, isLoading } = useQuery({
    queryKey: ["applicationProvenance", module, entryId],
    queryFn: () => fetchApplicationProvenance(module, entryId),
    enabled: Boolean(application) && canReview !== undefined && Boolean(module) && Boolean(entryId),
    refetchOnWindowFocus: false,
  });

  const refresh = () => {
    queryClient.invalidateQueries(["clientApplications", clientId]);
    queryClient.invalidateQueries(["applicationProvenance", module, entryId]);
  };

  const toggle = async (event) => {
    const visible = event.target.checked;
    setBusy(true);
    try {
      await setApplicationVisibility(module, entryId, visible);
      toast.success(
        visible
          ? "Application is now shared with the referral portal"
          : "Application is now hidden from the referral portal"
      );
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.errorMessage || "Could not change sharing");
    } finally {
      setBusy(false);
    }
  };

  const requested = provenance?.origin === "REQUESTED";

  return (
    <Dialog open={Boolean(application)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle className="flex items-start justify-between gap-3 !pr-3">
        <div className="min-w-0">
          <Typography variant="overline" color="text.secondary" className="!leading-none">
            Entry origin
          </Typography>
          <div className="!mt-2 flex items-center gap-2 flex-wrap">
            {requested ? (
              <Chip
                size="small"
                color="primary"
                icon={<History fontSize="small" />}
                label="Requested by referrer"
              />
            ) : (
              <Chip
                size="small"
                icon={<PersonOutline fontSize="small" />}
                label="Entered directly by staff"
              />
            )}
            {provenance && (
              <Chip
                size="small"
                variant="outlined"
                color={provenance.sharedWithPartner ? "success" : "default"}
                icon={
                  provenance.sharedWithPartner ? (
                    <Visibility fontSize="small" />
                  ) : (
                    <VisibilityOff fontSize="small" />
                  )
                }
                label={provenance.sharedWithPartner ? "Shared with portal" : "Hidden from portal"}
              />
            )}
          </div>
        </div>
        <IconButton aria-label="Close" onClick={onClose} size="small">
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers className="!bg-app-surface-alt">
        {isLoading && (
          <Typography variant="body2" color="text.secondary">
            Loading origin…
          </Typography>
        )}
        {provenance && (
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 m-0">
            {requested ? (
              <>
                <ProvenanceLine
                  label="Requested by"
                  who={provenance.requestedBy}
                  when={provenance.requestedAt}
                />
                <ProvenanceLine
                  label="Approved by"
                  who={provenance.approvedBy}
                  when={provenance.approvedAt}
                />
                <ProvenanceLine
                  label="Entry created by"
                  who={provenance.createdByName}
                  when={provenance.createdAt}
                />
              </>
            ) : (
              <ProvenanceLine
                label="Created by"
                who={provenance.createdByName}
                when={provenance.createdAt}
              />
            )}
          </dl>
        )}
        {canReview && provenance && (
          <div className="mt-4 rounded-md border border-app-border bg-app-surface px-3 py-2">
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(provenance.sharedWithPartner)}
                  onChange={toggle}
                  disabled={busy}
                  size="small"
                />
              }
              label={
                <span className="text-sm text-app-text">
                  Show this entry in the referral portal
                </span>
              }
            />
            <Typography variant="caption" color="text.secondary" className="!block !mt-1">
              {provenance.sharedWithPartner
                ? "The partner sees this application and its status history. Switching off hides it immediately."
                : "The partner cannot see this application. Switching on shares it with its status history."}
            </Typography>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ApplicationProvenanceDialog;
